// @ts-check
// Timeline do registro (doc 10, seção 8): eventos agrupados por mês, frases por ação e
// diff antes/depois nas atualizações.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { findRecordsByIds, findTimeline } from '../../data/rest.js';
import { mutationCounterState } from '../../data/record-store.js';
import { currentWorkspaceMemberState, objectsByNameSingular } from '../../data/metadata-store.js';
import { FieldDisplay } from '../object-record/field-display.js';
import { formatFullName, getFieldIcon } from '../../lib/field-metadata.js';
import { formatDateTime, formatMonthLabel, formatRelativeToNow, formatShortDate } from '../../lib/format.js';
import { classifyTimelineEvent, getTimelineEventLink } from '../../lib/timeline-events.js';
import { isModifiedClick } from '../../lib/record-panel.js';
import { Link } from '../../app/router.js';
import { openRecord } from '../side-panel/open-record.js';
import { Icon } from '../../ui/icon.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { EmailMarketingTimelineEvent } from './email-marketing-timeline-event.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 */

const RELATED_RECORD_ACTIONS = new Set(['created', 'updated', 'deleted']);

const ACTION_ICONS = {
  created: 'IconCirclePlus',
  updated: 'IconEditCircle',
  deleted: 'IconTrash',
  restored: 'IconRestore',
  linked: 'IconLink',
  unlinked: 'IconUnlink',
  // fusão de duplicatas (contrato 08, seção 2)
  merged: 'IconArrowMerge',
};

/**
 * @param {any} event
 */
function getEventAction(event) {
  const action = event.action ?? event.timelineActivityTypeSnapshot?.action ?? String(event.name ?? '').split('.').pop();
  return ACTION_ICONS[action] ? action : 'linked';
}

/**
 * @param {any} event
 */
function getAuthorName(event) {
  const currentMemberId = currentWorkspaceMemberState.value?.id;
  if (event.workspaceMemberId && event.workspaceMemberId === currentMemberId) return t('timeline.you');
  const member = event.workspaceMember;
  const name = member ? formatFullName(member.name) : event.authorName;
  return name || t('timeline.system');
}

/**
 * @param {ObjectMetadata} objectMetadata
 * @param {string} diffKey
 */
function findDiffField(objectMetadata, diffKey) {
  return (
    objectMetadata.fields.find((field) => field.name === diffKey) ??
    objectMetadata.fields.find((field) => `${field.name}Id` === diffKey)
  );
}

// o diff guarda só os ids da relação; os registros apontados são buscados uma vez e reaproveitados
/** @type {Map<string, Promise<any | null>>} */
const relatedRecordCache = new Map();

/**
 * @param {string | undefined} objectNameSingular
 * @param {unknown} id
 */
function useRelatedRecord(objectNameSingular, id) {
  const [related, setRelated] = useState(/** @type {any | null} */ (null));
  useEffect(() => {
    const targetObject = objectNameSingular ? objectsByNameSingular.value.get(objectNameSingular) : undefined;
    if (!targetObject || typeof id !== 'string' || !id) return undefined;
    const cacheKey = `${objectNameSingular}:${id}`;
    if (!relatedRecordCache.has(cacheKey)) {
      relatedRecordCache.set(cacheKey, findRecordsByIds(targetObject, [id]).then((records) => records[0] ?? null).catch(() => null));
    }
    let isCancelled = false;
    relatedRecordCache.get(cacheKey)?.then((record) => {
      if (!isCancelled) setRelated(record);
    });
    return () => {
      isCancelled = true;
    };
  }, [objectNameSingular, id]);
  return related;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, diffKey: string, diff: { before: unknown, after: unknown } }} props
 */
function DiffRow({ objectMetadata, diffKey, diff }) {
  const field = findDiffField(objectMetadata, diffKey);
  const isRelationDiff = Boolean(field) && field?.name !== diffKey;
  const targetName = isRelationDiff ? field?.relation?.targetObjectNameSingular : undefined;
  const relatedBefore = useRelatedRecord(targetName, diff.before);
  const relatedAfter = useRelatedRecord(targetName, diff.after);
  if (!field) {
    return html`<div class="timeline-diff-row">
      <span class="timeline-diff-label">${diffKey}</span>
      <span class="timeline-diff-before">${formatRawValue(diff.before)}</span>
      <span class="timeline-diff-arrow">→</span>
      <span class="timeline-diff-after">${formatRawValue(diff.after)}</span>
    </div>`;
  }
  const isRelationId = field.name !== diffKey;
  const beforeRecord = isRelationId ? { [`${field.name}Id`]: diff.before, [field.name]: relatedBefore } : { [field.name]: diff.before };
  const afterRecord = isRelationId ? { [`${field.name}Id`]: diff.after, [field.name]: relatedAfter } : { [field.name]: diff.after };
  return html`<div class="timeline-diff-row">
    <span class="timeline-diff-label"><${Icon} name=${getFieldIcon(field)} size=${14} />${translateMetadataLabel(field.label)}</span>
    <span class="timeline-diff-before"><${FieldDisplay} field=${field} record=${beforeRecord} emptyLabel=${t('common.empty')} /></span>
    <span class="timeline-diff-arrow">→</span>
    <span class="timeline-diff-after"><${FieldDisplay} field=${field} record=${afterRecord} emptyLabel=${t('common.empty')} /></span>
  </div>`;
}

/**
 * @param {unknown} value
 */
function formatRawValue(value) {
  if (value === null || value === undefined || value === '') return t('common.empty');
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/**
 * Nome clicável do registro ligado ao evento: nota e tarefa abrem no painel, conversa e agenda nas telas próprias.
 * @param {{ event: any, objectMetadata: ObjectMetadata, recordId: string, text: string }} props
 */
function TimelineEventLink({ event, objectMetadata, recordId, text }) {
  const href = getTimelineEventLink(event, { targetObjectNameSingular: objectMetadata.nameSingular, targetRecordId: recordId });
  if (!href || !text) return html`<span class="timeline-subject">${text}</span>`;
  const { kind, objectNameSingular } = classifyTimelineEvent(event);
  return html`<${Link} href=${href} className="timeline-subject timeline-subject-link" onClick=${(/** @type {MouseEvent} */ clickEvent) => {
    if (kind !== 'activity' || isModifiedClick(clickEvent) || !event.linkedRecordId) return;
    clickEvent.preventDefault();
    openRecord({ objectNameSingular, recordId: event.linkedRecordId, openIn: 'SIDE_PANEL', source: /** @type {Element} */ (clickEvent.currentTarget) });
  }}>${text}<//>`;
}

/**
 * Conversa (mensagens de um dia agrupadas) e evento de agenda: frase, cartão com o trecho e link para abrir.
 * @param {{ objectMetadata: ObjectMetadata, recordId: string, event: any, author: string }} props
 */
function RelatedActivitySentence({ objectMetadata, recordId, event, author }) {
  const { kind, action } = classifyTimelineEvent(event);
  const properties = event.properties ?? {};
  if (kind === 'conversation') {
    const channel = t(`recordPage.timeline.channels.${properties.channelType === 'EMAIL' ? 'EMAIL' : 'WHATSAPP'}`);
    const contact = properties.contactName || t('recordPage.timeline.unknownContact');
    // o htm junta elementos em linhas separadas sem espaço: o espaço entre as partes da frase vai explícito
    return html`
      <span class="timeline-event-sentence">
        <span class="timeline-author">${contact}</span>${' '}<span class="timeline-action">${t('recordPage.timeline.messages', { count: properties.messageCount ?? 1, channel })}</span>${' '}<${TimelineEventLink}
          event=${event} objectMetadata=${objectMetadata} recordId=${recordId} text=${t('recordPage.timeline.openConversation')} />
      </span>`;
  }
  const title = properties.isContentRestricted ? t('recordPage.timeline.privateEvent') : properties.title || t('recordPage.timeline.untitledEvent');
  return html`
    <span class="timeline-event-sentence">
      <span class="timeline-author">${author}</span>${' '}<span class="timeline-action">${t(action === 'updated' ? 'recordPage.timeline.calendarUpdated' : 'recordPage.timeline.calendarCreated')}</span>${' '}<${TimelineEventLink}
        event=${event} objectMetadata=${objectMetadata} recordId=${recordId} text=${title} />
    </span>`;
}

/**
 * @param {{ event: any }} props
 */
function RelatedActivityCard({ event }) {
  const { kind } = classifyTimelineEvent(event);
  const properties = event.properties ?? {};
  if (kind === 'conversation') {
    const text = properties.isContentRestricted ? t('recordPage.timeline.restrictedContent') : properties.excerpt;
    if (!text && !properties.subject) return null;
    return html`<div class="timeline-event-card timeline-message-card" data-direction=${properties.lastDirection ?? undefined}>
      ${properties.subject ? html`<span class="timeline-message-subject">${properties.subject}</span>` : null}
      ${text ? html`<span class="timeline-message-excerpt">${text}</span>` : null}
    </div>`;
  }
  if (!properties.startsAt) return null;
  return html`<div class="timeline-event-card timeline-calendar-card" data-canceled=${properties.isCanceled ? 'true' : undefined}>
    <${Icon} name="IconCalendarEvent" size=${14} />
    <span>${properties.isFullDay ? formatShortDate(properties.startsAt) : formatDateTime(properties.startsAt)}</span>
    ${properties.isCanceled ? html`<span class="timeline-calendar-canceled">${t('recordPage.timeline.canceled')}</span>` : null}
  </div>`;
}

const RELATED_ACTIVITY_ICONS = { conversation: 'IconMessages', calendar: 'IconCalendarEvent', attachment: 'IconPaperclip' };

/**
 * @param {{ objectMetadata: ObjectMetadata, recordId: string, event: any, recordLabel: string, isLast: boolean }} props
 */
function TimelineEvent({ objectMetadata, recordId, event, recordLabel, isLast }) {
  const [isExpanded, setIsExpanded] = useState(true);
  // e-mail de marketing (2.0.0): lido das entregas, sem evento gravado
  if (event.properties?.type === 'EMAIL_MARKETING') return html`<${EmailMarketingTimelineEvent} event=${event} isLast=${isLast} />`;
  const eventKind = classifyTimelineEvent(event);
  if (eventKind.kind === 'conversation' || eventKind.kind === 'calendar') {
    const icon = /** @type {Record<string, string>} */ (RELATED_ACTIVITY_ICONS)[eventKind.kind];
    return html`<div class="timeline-event" data-last=${isLast ? 'true' : undefined} data-kind=${eventKind.kind}>
      <div class="timeline-event-icon"><${Icon} name=${eventKind.kind === 'conversation' && event.properties?.channelType === 'EMAIL' ? 'IconMail' : icon} size=${16} /></div>
      <div class="timeline-event-content">
        <div class="timeline-event-line">
          <${RelatedActivitySentence} objectMetadata=${objectMetadata} recordId=${recordId} event=${event} author=${getAuthorName(event)} />
          <span class="timeline-event-date" title=${formatDateTime(event.happensAt)}>${formatRelativeToNow(event.happensAt)}</span>
        </div>
        <${RelatedActivityCard} event=${event} />
      </div>
    </div>`;
  }
  const action = getEventAction(event);
  const author = getAuthorName(event);
  const diffEntries = Object.entries(event.properties?.diff ?? {}).filter(([key]) => key !== 'position' && key !== 'updatedAt' && key !== 'searchVector');
  const linkedName = event.linkedRecordCachedName ?? '';
  // Eventos de filhos na timeline do pai (spec 04, R5): "adicionou Ana Silva / Ensino Médio".
  const eventObjectName = String(event.name ?? '').split('.')[0];
  const relatedObject = eventObjectName && eventObjectName !== objectMetadata.nameSingular && event.linkedRecordId && RELATED_RECORD_ACTIONS.has(action)
    ? objectsByNameSingular.value.get(eventObjectName)
    : undefined;
  const diffObject = relatedObject ?? objectMetadata;
  const relatedName = linkedName || (relatedObject ? translateMetadataLabel(relatedObject.labelSingular) : '');
  const relatedSuffix = relatedObject
    ? html`${' '}<span class="timeline-action">${t('timeline.relatedIn')}</span>${' '}<${TimelineEventLink} event=${event} objectMetadata=${objectMetadata} recordId=${recordId} text=${relatedName} />`
    : null;

  let sentence;
  if (action === 'updated' && diffEntries.length === 1) {
    const field = findDiffField(diffObject, diffEntries[0][0]);
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t('timeline.updatedField')}</span>${' '}<span class="timeline-subject">${field ? translateMetadataLabel(field.label) : diffEntries[0][0]}</span>${relatedSuffix}`;
  } else if (action === 'updated' && diffEntries.length > 1) {
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t('timeline.updatedFields', { count: diffEntries.length })}</span>${relatedSuffix}`;
  } else if (relatedObject && (action === 'created' || action === 'deleted')) {
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t(action === 'created' ? 'timeline.relatedCreated' : 'timeline.relatedDeleted')}</span>${' '}<span class="timeline-subject">${relatedName}</span>`;
  } else if (action === 'merged') {
    const mergedNames = (event.properties?.mergedRecords ?? []).map((/** @type {any} */ item) => item.label).filter(Boolean).join(', ');
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t('parity.merge.timeline', { count: event.properties?.mergedRecords?.length ?? 0 })}</span>${' '}<span class="timeline-subject">${mergedNames}</span>`;
  } else if (eventKind.kind === 'attachment' && (action === 'linked' || action === 'unlinked')) {
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t(action === 'linked' ? 'recordPage.timeline.attachmentLinked' : 'recordPage.timeline.attachmentUnlinked')}</span>${' '}<${TimelineEventLink} event=${event} objectMetadata=${objectMetadata} recordId=${recordId} text=${linkedName} />`;
  } else if (action === 'linked' || action === 'unlinked') {
    const linkedKey = eventKind.kind === 'activity' ? `recordPage.timeline.${eventKind.objectNameSingular}${action === 'linked' ? 'Linked' : 'Unlinked'}` : `timeline.${action}`;
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t(linkedKey)}</span>${' '}<${TimelineEventLink} event=${event} objectMetadata=${objectMetadata} recordId=${recordId} text=${linkedName || t('record.untitled')} />`;
  } else {
    sentence = html`<span class="timeline-author">${author}</span> <span class="timeline-action">${t(`timeline.${action}`)}</span>${' '}<span class="timeline-subject">${recordLabel}</span>`;
  }

  return html`<div class="timeline-event" data-last=${isLast ? 'true' : undefined}>
    <div class="timeline-event-icon"><${Icon} name=${ACTION_ICONS[action]} size=${16} /></div>
    <div class="timeline-event-content">
      <div class="timeline-event-line">
        <span class="timeline-event-sentence">${sentence}</span>
        ${action === 'updated' && diffEntries.length > 1
          ? html`<button type="button" class="light-icon-button" data-accent="tertiary" data-size="small"
              aria-label=${isExpanded ? t('timeline.collapse') : t('timeline.expand')} onClick=${() => setIsExpanded(!isExpanded)}>
              <${Icon} name=${isExpanded ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />
            </button>`
          : null}
        <span class="timeline-event-date" title=${formatDateTime(event.happensAt ?? event.createdAt)}>${formatRelativeToNow(event.happensAt ?? event.createdAt)}</span>
      </div>
      ${action === 'updated' && diffEntries.length > 0 && (diffEntries.length === 1 || isExpanded)
        ? html`<div class="timeline-event-card">
            ${diffEntries.map(([key, diff]) => html`<${DiffRow} key=${key} objectMetadata=${diffObject} diffKey=${key} diff=${/** @type {any} */ (diff)} />`)}
          </div>`
        : null}
    </div>
  </div>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, recordId: string, recordLabel: string }} props
 */
export function TimelineTab({ objectMetadata, recordId, recordLabel }) {
  const [events, setEvents] = useState(/** @type {any[] | null} */ (null));
  const [pageInfo, setPageInfo] = useState(/** @type {{ hasNextPage?: boolean, endCursor?: string | null }} */ ({}));
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadMore = async () => {
    if (!pageInfo.hasNextPage || !pageInfo.endCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const result = await findTimeline(objectMetadata.nameSingular, recordId, { startingAfter: pageInfo.endCursor });
      setEvents((current) => [...(current ?? []), ...result.events.filter((event) => !(current ?? []).some((known) => known.id === event.id))]);
      setPageInfo(result.pageInfo);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    let isCancelled = false;
    findTimeline(objectMetadata.nameSingular, recordId)
      .then((result) => {
        if (!isCancelled) {
          setEvents(result.events);
          setPageInfo(result.pageInfo);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setEvents([]);
          showErrorSnackbar(error);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [objectMetadata.nameSingular, recordId, mutationCounterState.value]);

  if (events === null) {
    return html`<div class="timeline">${[0, 1, 2].map((index) => html`<div key=${index} class="timeline-skeleton"><${Skeleton} width=${16} height=${16} /><${Skeleton} width="60%" height=${14} /></div>`)}</div>`;
  }
  if (events.length === 0) {
    return html`<${EmptyState} icon="IconTimelineEvent" title=${t('timeline.emptyTitle')} subtitle=${t('timeline.emptySubtitle')} />`;
  }

  const sortedEvents = [...events].sort((first, second) => String(second.happensAt ?? second.createdAt).localeCompare(String(first.happensAt ?? first.createdAt)));
  /** @type {{ key: string, date: Date, events: any[] }[]} */
  const groups = [];
  for (const event of sortedEvents) {
    const date = new Date(event.happensAt ?? event.createdAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.key === key) lastGroup.events.push(event);
    else groups.push({ key, date, events: [event] });
  }

  return html`<div class="timeline">
    ${groups.map((group, groupIndex) => {
      const previousYear = groupIndex > 0 ? groups[groupIndex - 1].date.getFullYear() : null;
      const showYear = groupIndex === 0 || previousYear !== group.date.getFullYear();
      return html`<div key=${group.key} class="timeline-group">
        <div class="timeline-month">
          <span>${formatMonthLabel(group.date, showYear)}</span>
          <span class="timeline-month-line"></span>
        </div>
        <div class="timeline-group-events">
          ${group.events.map((event, index) => html`<${TimelineEvent}
            key=${event.id}
            recordId=${recordId}
            objectMetadata=${objectMetadata}
            event=${event}
            recordLabel=${recordLabel}
            isLast=${index === group.events.length - 1}
          />`)}
        </div>
      </div>`;
    })}
    ${pageInfo.hasNextPage
      ? html`<div class="timeline-load-more"><${Button} title=${t('recordPage.timeline.loadMore')} variant="secondary" size="small" isLoading=${isLoadingMore} onClick=${loadMore} /></div>`
      : null}
  </div>`;
}
